import { useState } from 'react';
import { Link } from 'react-router-dom';
import { promoOffers, type PromoOffer } from '@/mocks/promos';
import PromoCountdown from './PromoCountdown';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const BannerCard = ({ offer, large }: { offer: PromoOffer; large?: boolean }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    if (offer.code) {
      navigator.clipboard.writeText(offer.code).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className={`relative rounded-2xl overflow-hidden group cursor-pointer ${large ? 'col-span-2' : ''}`}
      style={{ border: `1px solid ${offer.color}20`, minHeight: large ? 320 : 260 }}
    >
      {/* Background image */}
      <img
        src={offer.image}
        alt={offer.title}
        className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
      />
      {/* Gradient overlay */}
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(135deg, rgba(5,5,8,0.92) 0%, rgba(5,5,8,0.6) 60%, rgba(5,5,8,0.3) 100%)` }}
      />
      {/* Color glow */}
      <div
        className="absolute top-0 right-0 w-64 h-64 opacity-20 rounded-full"
        style={{ background: offer.color, filter: 'blur(80px)', transform: 'translate(30%, -30%)' }}
      />

      {/* Content */}
      <div className="relative z-10 p-6 h-full flex flex-col justify-between" style={{ minHeight: large ? 320 : 260 }}>
        <div>
          {/* Badge */}
          <span
            className="inline-block px-3 py-1 rounded-full font-mono text-[10px] font-bold tracking-wider mb-3"
            style={{ background: offer.badgeColor, color: '#050508', boxShadow: `0 0 10px ${offer.badgeColor}` }}
          >
            {offer.badge}
          </span>

          {/* Discount pill */}
          <div className="flex items-start gap-4 mb-3">
            <div>
              <h2
                className={`font-orbitron font-black text-white leading-tight mb-1 ${large ? 'text-2xl md:text-3xl' : 'text-xl'}`}
                style={{ textShadow: `0 0 20px ${offer.color}40` }}
              >
                {offer.title}
              </h2>
              <p className="font-mono text-sm" style={{ color: offer.color }}>{offer.subtitle}</p>
            </div>
            <div
              className="shrink-0 w-16 h-16 flex items-center justify-center rounded-2xl font-orbitron font-black text-xl"
              style={{
                background: `${offer.color}20`,
                border: `2px solid ${offer.color}`,
                color: offer.color,
                boxShadow: `0 0 20px ${offer.color}40`,
              }}
            >
              {offer.discount}
            </div>
          </div>

          {large && (
            <p className="font-mono text-xs text-white/40 leading-relaxed max-w-md mb-4">{offer.description}</p>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {/* Countdown */}
          <PromoCountdown endsAt={offer.endsAt} color={offer.color} />

          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to={`/promos/${offer.id}`}
              className="whitespace-nowrap px-5 py-2.5 rounded-full font-orbitron font-bold text-xs tracking-wider cursor-pointer transition-all"
              style={{ background: offer.color, color: '#050508', boxShadow: `0 0 15px ${offer.color}60` }}
            >
              ПЕРЕЙТИ К АКЦИИ
            </Link>
            {offer.code && (
              <button
                onClick={handleCopy}
                className="whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-full font-mono text-xs cursor-pointer transition-all"
                style={{ border: `1px solid ${offer.color}50`, color: offer.color }}
              >
                <i className={copied ? 'ri-check-line' : 'ri-coupon-line'} />
                {copied ? 'Скопировано!' : `Промокод: ${offer.code}`}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Top accent line */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5"
        style={{ background: `linear-gradient(90deg, ${offer.color}, ${offer.colorSecondary}, transparent)` }}
      />
    </div>
  );
};

const PromoBanners = () => {
  const { ref: sectionRef, isVisible } = useScrollReveal({ threshold: 0.1 });

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="px-6 lg:px-12 pb-16"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Large first banner */}
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
            }}
          >
            <BannerCard offer={promoOffers[0]} large />
          </div>
          {/* Two smaller banners */}
          <div className="flex flex-col gap-5">
            {[promoOffers[1], promoOffers[2]].map((offer, i) => (
              <div
                key={offer.id}
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateX(0)' : 'translateX(40px)',
                  transition: `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${0.15 + i * 0.12}s, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${0.15 + i * 0.12}s`,
                }}
              >
                <BannerCard offer={offer} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PromoBanners;
